import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import Typography from '../../../atoms/Typography';
import TextInput from '../../../atoms/TextInput';
import Button from '../../../atoms/Button';
import EntityDefinitionsContext from '../../../../contexts/EntityDefinitionsContext';
import useEntity from '../../../../hooks/useEntity';
import Dropdown from '../../../../components/Inputs/Dropdown';
import isDefined from '../../../../isDefined';

const EntityLinksDiv = styled.div`
  background-color: var(--color-bg-secondary);
  padding: 16px;
  gap: 16px;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: var(--color-grid-border);
`;

const EntityLinksGridDiv = styled.div`
  margin-bottom: 16px;
`;

const RowDiv = styled.div<{ isHeaders?: boolean }>`
  display: flex;
  flex-direction: row;

  border-width: 1px 1px 0 1px;
  &:last-of-type {
    border-bottom-width: 1px;
  }
  border-style: solid;
  border-color: var(--color-border-primary);

  background-color: ${(props) =>
    props.isHeaders ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)'};

  width: 100%;
  height: ${(props) => (props.isHeaders ? 40 : 48)}px;
  gap: 8px;
`;

const ColDiv = styled.div<{ isEnd?: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: ${(props) => (props.isEnd ? '32px' : '100%')};
  padding-left: 8px;
  padding-right: 8px;
`;

const EntityLinkInput = ({
  link,
  setEntityLinks,
}: {
  link: EngagementPortalEntityLink;
  setEntityLinks: React.Dispatch<
    React.SetStateAction<EngagementPortalEntityLink[]>
  >;
}) => {
  const { entity } = useEntity({ entityId: link.entityId });

  const onChangeNavLabel = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEntityLinks((currentLinks) => {
        return currentLinks.map((cl) => {
          if (cl.entityAppId === link.entityAppId) {
            return {
              ...cl,
              navLabel: event.target.value,
            };
          }

          return cl;
        });
      });
    },
    [link.entityAppId, setEntityLinks],
  );

  const onChangeUsersLabel = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEntityLinks((currentLinks) => {
        return currentLinks.map((cl) => {
          if (cl.entityAppId === link.entityAppId) {
            return {
              ...cl,
              usersTabLabel: event.target.value,
            };
          }

          return cl;
        });
      });
    },
    [link.entityAppId, setEntityLinks],
  );

  const onRemoveClicked = useCallback(() => {
    setEntityLinks((currentLinks) => {
      return currentLinks.filter((cl) => {
        return cl.entityAppId !== link.entityAppId;
      });
    });
  }, [link.entityAppId, setEntityLinks]);

  if (!entity) {
    return null;
  }

  return (
    <RowDiv>
      <ColDiv>
        <Typography.Body type={'Body 13'}>{entity.name}</Typography.Body>
      </ColDiv>
      <ColDiv>
        <TextInput
          value={link.navLabel}
          onChange={onChangeNavLabel}
          data-testid={'entity-nav-label'}
        />
      </ColDiv>
      <ColDiv>
        <TextInput
          value={link.usersTabLabel}
          onChange={onChangeUsersLabel}
          data-testid={'entity-users-label'}
        />
      </ColDiv>
      <ColDiv isEnd>
        <Button
          size={'Small'}
          type={'Ghost'}
          onClick={onRemoveClicked}
          icon={'cross'}
        />
      </ColDiv>
    </RowDiv>
  );
};

const AddEntityButton = ({
  entityLinks,
  setEntityLinks,
}: {
  entityLinks: EngagementPortalEntityLink[];
  setEntityLinks: React.Dispatch<
    React.SetStateAction<EngagementPortalEntityLink[]>
  >;
}) => {
  const { entityDefinitions, entityDetailsApps } = useContext(
    EntityDefinitionsContext,
  );

  const options = useMemo((): DropdownOption[] => {
    return entityDetailsApps
      .map((app) => {
        const entity = entityDefinitions.find((e) => e.id === app.entityId);
        if (!entity) {
          return undefined;
        }

        const isAlreadyAdded = entityLinks.some(
          (l) => l.entityId === entity.id && l.entityAppId === app.id,
        );
        if (isAlreadyAdded) {
          return undefined;
        }

        return {
          label: `${app.title} (${entity.name})`,
          isSelected: false,
          onSelected: () => {
            setEntityLinks((currentLinks) => [
              ...currentLinks,
              {
                entityId: entity.id,
                entityAppId: app.id,
                navLabel: `${app.title} (${entity.name})`,
                usersTabLabel: `${app.title} (${entity.name})`,
              },
            ]);
          },
        };
      })
      .filter(isDefined);
  }, [entityDefinitions, entityDetailsApps, entityLinks, setEntityLinks]);

  return (
    <div style={{ width: 150 }}>
      <Dropdown options={options} label={'Add Entity'} />
    </div>
  );
};

const EntityLinksInput = ({
  entityLinks,
  setEntityLinks,
}: {
  entityLinks: EngagementPortalEntityLink[];
  setEntityLinks: React.Dispatch<
    React.SetStateAction<EngagementPortalEntityLink[]>
  >;
}) => {
  return (
    <EntityLinksDiv>
      <div style={{ marginBottom: 16 }}>
        <Typography.Body type={'Label'}>
          Select Entities to enable Resource Navigator views in this portal
        </Typography.Body>
      </div>
      {entityLinks.length > 0 && (
        <EntityLinksGridDiv>
          <RowDiv>
            <ColDiv>
              <Typography.Body type={'Label'}>Entity</Typography.Body>
            </ColDiv>
            <ColDiv>
              <Typography.Body type={'Label'}>Nav Label</Typography.Body>
            </ColDiv>
            <ColDiv>
              <Typography.Body type={'Label'}>Users Tab Label</Typography.Body>
            </ColDiv>
            <ColDiv isEnd />
          </RowDiv>
          {entityLinks.map((link) => (
            <EntityLinkInput
              link={link}
              setEntityLinks={setEntityLinks}
              key={link.entityAppId}
            />
          ))}
        </EntityLinksGridDiv>
      )}
      <AddEntityButton
        entityLinks={entityLinks}
        setEntityLinks={setEntityLinks}
      />
    </EntityLinksDiv>
  );
};

export default EntityLinksInput;
